/**
 * AmenitiesList — Weidner Classic
 *
 * Tower 12 amenities page (https://www.tower12.com/amenities): two stacked
 * blocks (Community, then Apartment). Each block is a centered heading,
 * a 4-up landscape photo row, a 4-column disc-bullet list, and a mustard
 * CTA under that block (Schedule a Tour under Community; Find Your New
 * Home under Apartment — Tower 12 only shows the latter once at the end).
 *
 * Replaces the rise AmenitiesList (side-by-side photo slider + paginated
 * list). Same slot name so the inherited amenities page section still
 * resolves.
 *
 * Maintenance checklist when re-syncing with the rise parent:
 *   1. Diff this file against the rise AmenitiesList in the sibling checkout.
 *   2. Do not reintroduce the slider, pagination, or two-column row layout.
 *   3. Keep heading, then four photo+list columns (list under each photo).
 *   4. Keep the eight generated photo slot groups (4 community + 4
 *      apartment) and the Community / Apartment / allItems list bindings.
 *   5. Keep one CTA slot group under each amenity block.
 *   6. Run `pnpm run lint:schema` and `pnpm run typecheck`.
 */

import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useSlotGroup,
  useEditMode,
  EditableCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { useMemo } from 'react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ============================================================================
// Types
// ============================================================================

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
  [key: string]: unknown;
}

// ============================================================================
// Slot defs — photo tiles (literals; extractor cannot follow a factory)
// ============================================================================

const communityPhoto1: SlotGroupDefinition = {
  label: 'Community Photo 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A photo of a shared community space that actually exists at this property — lounge, lobby, rooftop, fitness, or another real amenity space. Do not invent a space.',
    context:
      'Photo 1 of 4 in the Community Amenities landscape grid on the Amenities page. Different from community photos 2–4 and from the apartment-interior row below.',
  },
};

const communityPhoto2: SlotGroupDefinition = {
  label: 'Community Photo 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Another unused photo of a real shared community space at this property. Different space and photo from the other community tiles.',
    context:
      'Photo 2 of 4 in the Community Amenities landscape grid on the Amenities page. Do not reuse community photos 1, 3, or 4.',
  },
};

const communityPhoto3: SlotGroupDefinition = {
  label: 'Community Photo 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A third unused photo of a real shared community space at this property. Different space and photo from the other community tiles.',
    context:
      'Photo 3 of 4 in the Community Amenities landscape grid on the Amenities page. Do not reuse community photos 1, 2, or 4.',
  },
};

const communityPhoto4: SlotGroupDefinition = {
  label: 'Community Photo 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A fourth unused photo of a real shared community space at this property. Different space and photo from the other community tiles.',
    context:
      'Photo 4 of 4 in the Community Amenities landscape grid on the Amenities page. Do not reuse community photos 1–3.',
  },
};

const apartmentPhoto1: SlotGroupDefinition = {
  label: 'Apartment Photo 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A photo of a real apartment interior at this property — kitchen, bedroom, bathroom, or living area. Do not invent finishes or rooms.',
    context:
      'Photo 1 of 4 in the Apartment Amenities landscape grid on the Amenities page. Different from apartment photos 2–4 and from the community-space row above.',
  },
};

const apartmentPhoto2: SlotGroupDefinition = {
  label: 'Apartment Photo 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 5,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Another unused apartment-interior photo from this property. Different room and photo from the other apartment tiles.',
    context:
      'Photo 2 of 4 in the Apartment Amenities landscape grid on the Amenities page. Do not reuse apartment photos 1, 3, or 4.',
  },
};

const apartmentPhoto3: SlotGroupDefinition = {
  label: 'Apartment Photo 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[6].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[6].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 6,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A third unused apartment-interior photo from this property. Different room and photo from the other apartment tiles.',
    context:
      'Photo 3 of 4 in the Apartment Amenities landscape grid on the Amenities page. Do not reuse apartment photos 1, 2, or 4.',
  },
};

const apartmentPhoto4: SlotGroupDefinition = {
  label: 'Apartment Photo 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[7].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[7].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 7,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A fourth unused apartment-interior photo from this property. Different room and photo from the other apartment tiles.',
    context:
      'Photo 4 of 4 in the Apartment Amenities landscape grid on the Amenities page. Do not reuse apartment photos 1–3.',
  },
};

const communityCtaDef: SlotGroupDefinition = {
  label: 'Community CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
    vibesite_event: {
      label: 'Tracked Event',
      type: 'event',
      defaultValue: 'schedule_tour_clicked',
    },
  },
};

const apartmentCtaDef: SlotGroupDefinition = {
  label: 'Apartment CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Find Your New Home' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Helpers
// ============================================================================

function splitColumns<T>(items: T[], columnCount: number): T[][] {
  const cols: T[][] = Array.from({ length: columnCount }, () => []);
  if (items.length === 0) return cols;
  const perCol = Math.ceil(items.length / columnCount);
  items.forEach((item, i) => {
    cols[Math.min(columnCount - 1, Math.floor(i / perCol))].push(item);
  });
  return cols;
}

function groupName(group?: string | null): string {
  return (group || '').trim();
}

function isCommunityGroup(group?: string | null): boolean {
  const g = groupName(group).toLowerCase();
  return g === 'community' || g.startsWith('community ') || g.includes('community');
}

function isApartmentGroup(group?: string | null): boolean {
  const g = groupName(group).toLowerCase();
  if (isCommunityGroup(group)) return false;
  return g === 'apartment' || g.startsWith('apartment');
}

// ============================================================================
// Photo tile
// ============================================================================

function PhotoTile({
  path,
  definition,
}: {
  path: string;
  definition: SlotGroupDefinition;
}) {
  const photo = useSlotGroup(path, definition);
  const editMode = useEditMode();
  const isEditing = !!editMode?.isEditMode;
  const src = photo.image || '';

  if (!src && !isEditing) return null;

  return (
    <div className="amenities-list__photo">
      <EditableSlotGroup groupPath={path} definition={definition}>
        {src ? (
          <VibesitesImage
            url={src}
            alt={photo.alt || ''}
            sizes="(min-width: 1200px) 22vw, (min-width: 768px) 23vw, 45vw"
            className="amenities-list__photo-img"
          />
        ) : (
          <div className="amenities-list__photo-placeholder" aria-hidden="true" />
        )}
      </EditableSlotGroup>
    </div>
  );
}

function AmenityBand({
  items,
  bindingPath,
  label,
  photos,
}: {
  items: Amenity[];
  bindingPath: string;
  label: string;
  photos: { path: string; definition: SlotGroupDefinition }[];
}) {
  const columns = splitColumns(items, photos.length);

  return (
    <EditableCollectionBinding
      bindingPath={bindingPath}
      label={label}
      className="amenities-list__band"
    >
      <div className="amenities-list__pairs">
        {photos.map((photo, colIdx) => {
          const col = columns[colIdx] ?? [];
          return (
            <div key={photo.path} className="amenities-list__pair">
              <PhotoTile path={photo.path} definition={photo.definition} />
              {col.length > 0 ? (
                <ul className="amenities-list__col">
                  {col.map((item, i) => (
                    <li key={`${colIdx}-${item.title || i}`} className="amenities-list__item">
                      {item.title || ''}
                    </li>
                  ))}
                </ul>
              ) : colIdx === 0 && items.length === 0 ? (
                <p className="amenities-list__empty">Amenities will be listed here.</p>
              ) : (
                <ul className="amenities-list__col" aria-hidden="true" />
              )}
            </div>
          );
        })}
      </div>
    </EditableCollectionBinding>
  );
}

// ============================================================================
// Component
// ============================================================================

export function AmenitiesList() {
  const communitySource = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const apartmentSource = useCollectionBinding<Amenity>('amenitieslist.apartmentItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  const allAmenities = useCollectionBinding<Amenity>('amenitieslist.allItems', {
    collection: 'amenities',
  });

  const communityItems = useMemo(() => {
    const titled = (items: Amenity[]) => (items ?? []).filter((item) => item?.title);
    const official = titled(communitySource);
    if (official.length > 0) return official;

    const pool = titled(allAmenities);
    const loose = pool.filter((item) => isCommunityGroup(item.group));
    if (loose.length > 0) return loose;

    const notApartment = pool.filter((item) => !isApartmentGroup(item.group));
    return notApartment.length > 0 ? notApartment : pool;
  }, [communitySource, allAmenities]);

  const apartmentItems = useMemo(() => {
    const official = (apartmentSource ?? []).filter((item) => item?.title);
    if (official.length > 0) return official;
    return (allAmenities ?? []).filter((item) => item?.title && isApartmentGroup(item.group));
  }, [apartmentSource, allAmenities]);

  const communityCta = useSlotGroup('amenitieslist.communityCta', communityCtaDef);
  const apartmentCta = useSlotGroup('amenitieslist.cta', apartmentCtaDef);

  return (
    <section className="amenities-list bg-background">
      <div className="amenities-list__stack">
        <div className="amenities-list__block">
          <RichTextContent
            path="amenitieslist.communityHeading"
            label="Community Heading"
            defaultValue="<p>Community Amenities</p>"
            as="h2"
            className="amenities-list__heading"
          />
          <AmenityBand
            items={communityItems}
            bindingPath="amenitieslist.communityItems"
            label="Community Amenities"
            photos={[
              { path: 'amenitieslist.communityPhoto1', definition: communityPhoto1 },
              { path: 'amenitieslist.communityPhoto2', definition: communityPhoto2 },
              { path: 'amenitieslist.communityPhoto3', definition: communityPhoto3 },
              { path: 'amenitieslist.communityPhoto4', definition: communityPhoto4 },
            ]}
          />
          <EditableSlotGroup
            groupPath="amenitieslist.communityCta"
            definition={communityCtaDef}
            className="amenities-list__cta-wrap"
          >
            <a
              href={communityCta.url || '/schedule-a-tour'}
              target={communityCta._target || '_self'}
              className="amenities-list__cta"
              data-vibesite-event={communityCta.vibesite_event || undefined}
            >
              <span>{communityCta.label || 'Schedule a Tour'}</span>
              <svg
                className="amenities-list__cta-icon"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M5 12h14M13 6l6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </EditableSlotGroup>
        </div>

        <div className="amenities-list__block">
          <RichTextContent
            path="amenitieslist.apartmentHeading"
            label="Apartment Heading"
            defaultValue="<p>Apartment Amenities</p>"
            as="h2"
            className="amenities-list__heading"
          />
          <AmenityBand
            items={apartmentItems}
            bindingPath="amenitieslist.apartmentItems"
            label="Apartment Amenities"
            photos={[
              { path: 'amenitieslist.apartmentPhoto1', definition: apartmentPhoto1 },
              { path: 'amenitieslist.apartmentPhoto2', definition: apartmentPhoto2 },
              { path: 'amenitieslist.apartmentPhoto3', definition: apartmentPhoto3 },
              { path: 'amenitieslist.apartmentPhoto4', definition: apartmentPhoto4 },
            ]}
          />
          <EditableSlotGroup
            groupPath="amenitieslist.cta"
            definition={apartmentCtaDef}
            className="amenities-list__cta-wrap"
          >
            <a
              href={apartmentCta.url || '/floor-plans'}
              target={apartmentCta._target || '_self'}
              className="amenities-list__cta"
            >
              <span>{apartmentCta.label || 'Find Your New Home'}</span>
              <svg
                className="amenities-list__cta-icon"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M5 12h14M13 6l6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <EditableCollectionBinding
        bindingPath="amenitieslist.allItems"
        label="All Amenities"
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableCollectionBinding>

      {/* Schema-lint anchors for photo tiles — PhotoTile passes groupPath
          as a variable, so these literal wrappers satisfy the linter. */}
      <EditableSlotGroup
        groupPath="amenitieslist.communityPhoto1"
        definition={communityPhoto1}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="amenitieslist.communityPhoto2"
        definition={communityPhoto2}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="amenitieslist.communityPhoto3"
        definition={communityPhoto3}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="amenitieslist.communityPhoto4"
        definition={communityPhoto4}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="amenitieslist.apartmentPhoto1"
        definition={apartmentPhoto1}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="amenitieslist.apartmentPhoto2"
        definition={apartmentPhoto2}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="amenitieslist.apartmentPhoto3"
        definition={apartmentPhoto3}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="amenitieslist.apartmentPhoto4"
        definition={apartmentPhoto4}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        .amenities-list {
          width: 100%;
          padding: 3.25rem 1.25rem 4.5rem;
          background: #fff;
          color: #000;
        }
        @media (min-width: 768px) {
          .amenities-list { padding: 4rem 2.5rem 5.5rem; }
        }
        @media (min-width: 1200px) {
          .amenities-list { padding: 4rem 3.5rem 5.5rem; }
        }

        .amenities-list__stack {
          display: flex;
          flex-direction: column;
          gap: 3.5rem;
          max-width: 1400px;
          margin-inline: auto;
        }
        @media (min-width: 992px) {
          .amenities-list__stack { gap: 4.5rem; }
        }

        .amenities-list__block {
          display: flex;
          flex-direction: column;
        }

        .amenities-list__heading {
          margin: 0 0 1.5rem;
          font-family: var(--vibe-font-heading);
          font-size: 1.75rem;
          font-weight: 400;
          line-height: 1.15;
          letter-spacing: 0.02em;
          text-align: center;
          text-transform: uppercase;
          color: var(--vibe-color-primary);
        }
        .amenities-list__heading,
        .amenities-list__heading * {
          color: var(--vibe-color-primary) !important;
        }
        @media (min-width: 768px) {
          .amenities-list__heading {
            margin-bottom: 1.75rem;
            font-size: 2.5rem;
          }
        }
        @media (min-width: 1200px) {
          .amenities-list__heading { font-size: 3rem; }
        }
        .amenities-list__heading p { margin: 0; }

        .amenities-list__empty {
          margin: 1rem 0.75rem 0;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          color: #000;
        }

        .amenities-list__band {
          display: block;
          width: 100%;
        }
        .amenities-list__pairs {
          display: grid;
          grid-template-columns: 1fr 1fr;
          column-gap: 4px;
          row-gap: 1.75rem;
        }
        @media (min-width: 768px) {
          .amenities-list__pairs {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }
        }

        .amenities-list__pair {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .amenities-list__photo {
          display: block;
          margin: 0;
          position: relative;
          aspect-ratio: 4 / 3;
          overflow: hidden;
          background: var(--vibe-color-surface);
        }
        .amenities-list__photo > * {
          display: block;
          width: 100%;
          height: 100%;
        }
        .amenities-list__photo-img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }
        .amenities-list__photo-placeholder {
          width: 100%;
          height: 100%;
          background: var(--vibe-color-surface);
        }

        .amenities-list__col {
          list-style: disc;
          list-style-position: outside;
          margin: 1rem 0 0;
          padding: 0 0.65rem 0 1.35rem;
          color: #000;
        }
        .amenities-list__col::marker,
        .amenities-list__item::marker {
          color: #000;
        }
        .amenities-list__item {
          margin: 0 0 0.55rem;
          padding: 0;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          font-weight: 400;
          line-height: 1.55;
          color: #000;
        }
        @media (min-width: 768px) {
          .amenities-list__item { font-size: 1rem; }
        }

        .amenities-list__cta-wrap {
          display: flex;
          justify-content: center;
          margin: 2.5rem auto 0;
          max-width: 1400px;
        }
        @media (min-width: 768px) {
          .amenities-list__cta-wrap { margin-top: 3rem; }
        }

        .amenities-list__cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.65rem;
          min-width: 16.5rem;
          height: 60px;
          padding: 0 1.75rem;
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          text-decoration: none;
          border: none;
          border-radius: 0;
          transition: background 0.25s ease, color 0.25s ease;
        }
        .amenities-list__cta:hover {
          background: #b6b6b6;
          color: #000;
        }
        .amenities-list__cta-icon {
          flex-shrink: 0;
        }

        @media (prefers-reduced-motion: reduce) {
          .amenities-list__cta { transition: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.communityHeading': {
      label: 'Community Heading',
      type: 'richtext',
      defaultValue: '<p>Community Amenities</p>',
    },
    'amenitieslist.apartmentHeading': {
      label: 'Apartment Heading',
      type: 'richtext',
      defaultValue: '<p>Apartment Amenities</p>',
    },
  },
  slotGroups: {
    'amenitieslist.communityPhoto1': communityPhoto1,
    'amenitieslist.communityPhoto2': communityPhoto2,
    'amenitieslist.communityPhoto3': communityPhoto3,
    'amenitieslist.communityPhoto4': communityPhoto4,
    'amenitieslist.apartmentPhoto1': apartmentPhoto1,
    'amenitieslist.apartmentPhoto2': apartmentPhoto2,
    'amenitieslist.apartmentPhoto3': apartmentPhoto3,
    'amenitieslist.apartmentPhoto4': apartmentPhoto4,
    'amenitieslist.communityCta': communityCtaDef,
    'amenitieslist.cta': apartmentCtaDef,
  },
  collectionBindings: {
    'amenitieslist.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
      filter: { group: 'Community' },
    },
    'amenitieslist.apartmentItems': {
      label: 'Apartment Features',
      collection: 'amenities',
      filter: { group: 'Apartment' },
    },
    'amenitieslist.allItems': {
      label: 'All Amenities',
      collection: 'amenities',
      description: 'Fallback pool used when Community / Apartment group labels do not match exactly.',
    },
  },
};

(AmenitiesList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesList;
